import {
  CommercialMapPageClusterRequest,
  CommercialMapPageClusterResponse,
  CommercialSortBy,
  CommercialMapPageBoundarySearchRequest,
  CommercialMapPageBoundarySearchResponse,
} from '@zoocasa/go-search';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { isServerSide } from 'utils/host-config';
import { getGoSearchHost } from 'utils/host-config';
import { HttpRequestMethodTypes } from 'types';
import { Bounds } from 'components/search-es/types';
import { CommercialSort } from 'utils/select-options';

const sortByMap = {
  price: CommercialSortBy.SORT_BY_PRICE_ASC,
  '-price': CommercialSortBy.SORT_BY_PRICE_DESC,
  date: CommercialSortBy.SORT_BY_API_MODIFICATION_TIMESTAMP_ASC,
  '-date': CommercialSortBy.SORT_BY_API_MODIFICATION_TIMESTAMP_DESC,
};

const isAbortError = (error: unknown): boolean => {
  if (error instanceof Error && error.name !== 'AbortError') {
    return false;
  }
  
  return true;
};

// Retail, Office, Industrial, Hospitality, Multi-Family and Others
export type PropertyTypeKey = 'Retail' | 'Office' | 'Industrial' | 'Multi Family' | 'Other';

export const PROPERTY_TYPE_OPTIONS: Array<PropertyTypeKey> = ['Retail', 'Office', 'Industrial', 'Multi Family', 'Other'];

// Sale/Lease filter options
export type SaleLeaseKey = 'For Sale' | 'For Lease';
export const SALE_LEASE_OPTIONS: Array<SaleLeaseKey> = ['For Sale', 'For Lease'];

const DEFAULT_PAGE_SIZE = 24;

type Filter = {
  bounds: Bounds;
  precision: number;
  propertyTypes?: Array<PropertyTypeKey>;
  states?: Array<string>;
  cities?: Array<string>;
  page?: string;
  isSale?: boolean;
};

export const fetchClusters = async (filter: Filter, abortSignal?: AbortSignal) => {
  try {
    const host = getGoSearchHost(isServerSide());
    const { bounds, precision, propertyTypes, states, cities, isSale } = filter;

    const request = CommercialMapPageClusterRequest.fromPartial({
      filter: {
        filter: {
          propertySubTypes: propertyTypes,
          statesOrProvinces: states,
          cities: cities,
          isSale: isSale,
        },
        latLonAsArray: {
          topLeft: [bounds.north, bounds.west],
          bottomRight: [bounds.south, bounds.east],
        },
        precision,
      },
    });

    const params = new URLSearchParams();
    params.append('limit', DEFAULT_PAGE_SIZE.toString());
    const requestBody = CommercialMapPageClusterRequest.encode(request).finish();
    const response = await fetchWithRetry(`${host}/api/commercial/map/clusters?${params.toString()}`, { method: HttpRequestMethodTypes.POST, body: requestBody, signal: abortSignal });
    const content = await response.blob();
    const buffer = await content.arrayBuffer();
    return CommercialMapPageClusterResponse.decode(new Uint8Array(buffer));
  } catch (error: unknown) {
    if (!isAbortError(error)) {
      console.error('Failed to fetch clusters: %s', error);
    }
    return CommercialMapPageClusterResponse.fromPartial({});
  }
};

export const fetchListingsByBoundary = async (filter: Filter, sort: CommercialSort, abortSignal?: AbortSignal) => {
  try {
    const host = getGoSearchHost(isServerSide());
    const { bounds, precision, page, propertyTypes, states, cities, isSale } = filter;

    const request = CommercialMapPageBoundarySearchRequest.fromPartial({
      sortBy: sortByMap[sort] ?? CommercialSortBy.SORT_BY_UNSPECIFIED,
      filter: {
        filter: {
          propertySubTypes: propertyTypes,
          statesOrProvinces: states,
          cities: cities,
          isSale: isSale,
        },
        latLonAsArray: {
          topLeft: [bounds.north, bounds.west],
          bottomRight: [bounds.south, bounds.east],
        },
        precision,
      },
    });
    
    const params = new URLSearchParams();
    params.append('limit', DEFAULT_PAGE_SIZE.toString());
    if (page) {
      params.append('page', page);
    }
    const requestBody = CommercialMapPageClusterRequest.encode(request).finish();
    const response = await fetchWithRetry(`${host}/api/commercial/map/boundary?${params.toString()}`, { method: HttpRequestMethodTypes.POST, body: requestBody, signal: abortSignal });
    const content = await response.blob();
    const buffer = await content.arrayBuffer();
    return CommercialMapPageBoundarySearchResponse.decode(new Uint8Array(buffer));
  } catch (error: unknown) { 
    if (!isAbortError(error)) {
      console.error('Failed to fetch listings by boundary: %s', error);
    }
    return CommercialMapPageBoundarySearchResponse.fromPartial({});
  }
};